import React from "react";
import EbDialog from "./EbDialog";

// ═════════════════════════════════════════════════════════════════════════════
// "Remove this WIG?" — the confirmation in front of `delete_live_review_item`.
//
// ⚠️ IDENTICAL IN TWO REPOSITORIES. Imports only `EbDialog`, which is itself
// byte-identical, so it stays that way.
//
// ── Why this dialog exists at all ───────────────────────────────────────────
// Deleting a WIG is a soft delete server-side, but there is no undo ANYWHERE in
// this product's UI, so from the user's side it is permanent. That alone would
// justify a confirm.
//
// The real reason is the second sentence: deleting a WIG cascade-deletes every
// connector touching either of its ends, in the same transaction. That is
// invisible from the row being deleted — the connections live on a different
// screen entirely — and a coach who removes one WIG during stage 1 would find
// the map changed underneath them at stage 2 with no idea why. Naming the
// number here is the only place that fact can land before it happens.
// ═════════════════════════════════════════════════════════════════════════════

const TrashIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M4 7h16M10 4h4M9 7v12m6-12v12M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1L18 7"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * @param item            The WIG being removed. Its wording is quoted back, so
 *                        the dialog is about a specific thing rather than "this
 *                        item" — the one detail that stops a mis-click being
 *                        confirmed on autopilot.
 * @param connectionCount How many connectors will go with it. Omit or pass 0
 *                        and the clause about them is left out entirely.
 * @param busy            The delete is in flight.
 * @param onConfirm/onCancel
 */
const DeleteWigDialog = ({
  item,
  connectionCount = 0,
  busy = false,
  onConfirm,
  onCancel,
}) => {
  const wording = (item?.value || item?.label || "").trim();

  return (
    <EbDialog
      isOpen
      size="sm"
      tone="danger"
      icon={<TrashIcon />}
      title="Remove this WIG?"
      busy={busy}
      onClose={onCancel}
      actions={[
        { label: "Cancel", variant: "ghost", onClick: onCancel },
        {
          label: "Remove WIG",
          loadingLabel: "Removing…",
          variant: "danger",
          onClick: onConfirm,
          loading: busy,
        },
      ]}
    >
      {wording ? (
        <p className="ebd-quote">&ldquo;{wording}&rdquo;</p>
      ) : (
        <p>This WIG has no wording yet.</p>
      )}

      {/* Two facts, in the order they bite: what else goes, then that it is
          final. The connector clause is omitted rather than saying "0
          connections", which would draw attention to a thing that isn't
          happening. */}
      <p>
        {connectionCount > 0 ? (
          <>
            Its{" "}
            <strong>
              {connectionCount}{" "}
              {connectionCount === 1 ? "connection" : "connections"}
            </strong>{" "}
            on the WIG map will be removed with it. This can&apos;t be undone.
          </>
        ) : (
          <>This can&apos;t be undone.</>
        )}
      </p>
    </EbDialog>
  );
};

export default DeleteWigDialog;
